'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useLocale } from 'next-intl';
import { studioClient } from '@/sanity/lib/client';
import { localizedTermsAndConditionsQuery } from '@/sanity/queries/termsAndConditions';
import { ITermsAndConditions, ILocalizedTermsAndConditions } from '@/types/termsAndConditions';
import { LoadingSpinner } from '@/shared';
import objectiveEllipse from '@/assets/illustrations/objective-ellipse.svg';

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsAndConditionsModal: React.FC<TermsAndConditionsModalProps> = ({ isOpen, onClose }) => {
  const locale = useLocale();
  const [termsData, setTermsData] = useState<ILocalizedTermsAndConditions | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTermsData = async () => {
      try {
        const data = await studioClient.fetch<ITermsAndConditions>(
          localizedTermsAndConditionsQuery(locale)
        );

        if (data) {
          // Localize the data
          const localizedData: ILocalizedTermsAndConditions = {
            ...data,
            title: data.title[locale as keyof typeof data.title] || data.title.en,
            description:
              data.description[locale as keyof typeof data.description] || data.description.en,
            icon: {
              ...data.icon,
              alt: data.icon.alt[locale as keyof typeof data.icon.alt] || data.icon.alt.en,
            },
          };
          setTermsData(localizedData);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching terms and conditions data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchTermsData();
    }
  }, [isOpen, locale]);

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div className="relative z-10">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (!termsData) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={handleBackdropClick}>
      {/* Backdrop */}
      <div onClick={onClose} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal Content */}
      <div className="relative z-10 w-full max-w-xs sm:max-w-2xl md:max-w-4xl lg:max-w-6xl xl:max-w-7xl">
        <div
          className="relative flex max-h-[95vh] flex-col rounded-xl border border-white/20 bg-gradient-to-r from-white/10 to-white/0 p-4 backdrop-blur-sm sm:max-h-[90vh] sm:rounded-2xl sm:p-6 md:p-8 lg:rounded-3xl lg:p-12"
          style={{
            background:
              'linear-gradient(90deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.00) 100%)',
          }}>
          {/* Green left border - bigger version */}
          <div
            className="absolute top-12 h-12 w-1 sm:top-16 sm:h-16 sm:w-1.5 lg:top-20 lg:h-20 ltr:left-0 ltr:rounded-r-lg rtl:right-0 rtl:rounded-l-lg"
            style={{
              background: '#46E72D',
              boxShadow:
                '0 0 20px 4px rgba(74, 231, 59, 0.40), 0 0 30px 8px rgba(74, 231, 59, 0.20)',
            }}
          />

          {/* Header Section - Fixed */}
          <div className="flex-shrink-0">
            {/* Icon Section - bigger version */}
            {termsData.icon && (
              <div className="flex justify-center sm:justify-start">
                <div className="flex justify-center">
                  <div className="relative h-[120px] w-[120px] sm:h-[160px] sm:w-[160px] lg:h-[200px] lg:w-[200px]">
                    <Image
                      src={objectiveEllipse}
                      alt="objective-ellipse"
                      className="absolute h-[120px] w-[120px] sm:h-[160px] sm:w-[160px] lg:h-[200px] lg:w-[200px]"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative h-6 w-6 sm:h-8 sm:w-8 lg:h-9 lg:w-9">
                        <Image
                          src={termsData.icon.asset.url}
                          alt={termsData.icon.alt || termsData.title}
                          fill
                          className="object-contain"
                          sizes="(max-width: 640px) 24px, (max-width: 1024px) 32px, 36px"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Title - Fixed */}
            <h2 className="mb-4 text-center text-2xl leading-tight font-bold text-[#EDEDF2] sm:mb-6 sm:text-left sm:text-3xl md:text-4xl lg:text-5xl">
              {termsData.title}
            </h2>
          </div>

          {/* Content Section - Scrollable */}
          <div className="flex-1 overflow-y-auto pr-1 sm:pr-2">
            <div className="space-y-3 text-sm leading-relaxed text-[#EDEDF2B2] sm:space-y-4 sm:text-base md:text-lg lg:text-xl">
              {termsData.description.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-3 sm:mb-4">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsModal;
